import React, {useState, useEffect} from 'react';
import {Outlet, useParams, useSearchParams} from 'react-router-dom';

import {ChatHome} from './ChatHome';
import {ChatList} from './ChatList';
import {ChatInfo, ChatMoreInfo} from './ChatInfo';
import {ReviewControl} from './ReviewControl';
import {ClientInfoProvider} from '../ClientInfoProvider';
import {useIsAdmin} from '../state/hooks';
import {GoogleTranslateApiProvider} from '../Translate';
import {Grid} from '../components/ui/grid';
import {
  Sidebar,
  SidebarContent,
  SidebarInset,
  SidebarProvider,
} from '../components/ui/sidebar';

/**
 * Container for the chat thread.
 */
export const ChatWindow = () => {
  const screens = Grid.useBreakpoint();
  const isAdmin = useIsAdmin();
  const [collapsed, setCollapsed] = useState(true);
  const {accountId} = useParams();
  const [searchParams] = useSearchParams();

  // Check if we're in review mode (r query parameter present)
  const isReviewMode = searchParams.get('r') !== null;

  // Use a custom hook to determine whether the side bar should be collapsed.
  // We do this because the "Home" screen needs to show the chat list
  // regardless of the screen size (since it's the most important thing on the
  // screen at that point), but the "Chat" view can hide the chat list since
  // the chat is the most important thing in that case.
  useEffect(() => {
    if (!accountId) {
      setCollapsed(false);
      return;
    }

    setCollapsed(!screens.md);
  }, [accountId, screens.md]);

  return (
    <GoogleTranslateApiProvider>
      <SidebarProvider defaultOpen={!collapsed}>
        <div className="flex h-full w-full">
          <Sidebar
            side="left"
            variant="sidebar"
            collapsible={collapsed ? 'offcanvas' : 'none'}
            className="border-r"
          >
            <SidebarContent>
              <ChatList />
            </SidebarContent>
          </Sidebar>

          <SidebarInset className="flex-1 flex flex-col">
            {!accountId ? (
              <ChatHome />
            ) : (
              <ClientInfoProvider admin={isAdmin} accountId={accountId}>
                <div className="flex h-full">
                  <div className="flex-1 flex flex-col">
                    <div
                      id="nudge-chat-info"
                      className="border-b bg-background flex items-center justify-evenly w-full p-0 shrink-0"
                    >
                      <ChatInfo />
                    </div>
                    <div className="flex-1 overflow-auto flex flex-col">
                      {accountId ? <Outlet /> : null}
                    </div>
                  </div>

                  <Sidebar
                    side="right"
                    variant="sidebar"
                    collapsible={!screens.lg ? 'offcanvas' : 'none'}
                    className="border-l"
                  >
                    <SidebarContent className="flex flex-col h-full">
                      <div className="flex-1 overflow-auto">
                        <ChatMoreInfo />
                      </div>
                      {isReviewMode && (
                        <div className="border-t bg-background">
                          <ReviewControl />
                        </div>
                      )}
                    </SidebarContent>
                  </Sidebar>
                </div>
              </ClientInfoProvider>
            )}
          </SidebarInset>
        </div>
      </SidebarProvider>
    </GoogleTranslateApiProvider>
  );
};
